import { useEffect, useMemo, useState } from "react";
import { api, Payable } from "../api";
import { formatVND, formatDate } from "../utils";
import { GlassPanel, KpiCard, Badge, Modal, toast, SkeletonTable, ErrorState, DataTable, EmptyState } from "../components";
import type { Column } from "../components";

/** Trang Công nợ NCC (P1-11) — FULL */
export default function PayablesPage() {
  const [payables, setPayables] = useState<Payable[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [sortByDue, setSortByDue] = useState(false);
  // Payment modal
  const [showPayModal, setShowPayModal] = useState(false);
  const [selectedPayable, setSelectedPayable] = useState<Payable | null>(null);
  const [payAmount, setPayAmount] = useState(0);
  const [payMethod, setPayMethod] = useState("cash");
  const [payNote, setPayNote] = useState("");
  const [payAttachment, setPayAttachment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Detail modal
  const [detailPayable, setDetailPayable] = useState<Payable | null>(null);

  useEffect(() => {
    loadPayables();
  }, [statusFilter]);

  const loadPayables = async () => {
    setLoading(true);
    try {
      const r = await api.payables.list(statusFilter || undefined);
      setPayables(r.payables || []);
    } catch (e: unknown) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const totalOwed = payables.reduce((sum, p) => sum + (p.remaining || 0), 0);
  const overdue = payables.filter((p) => p.due_date && p.status !== "paid" && new Date(p.due_date) < new Date());
  const supplierCount = new Set(payables.map((p) => p.supplier_name)).size;

  const sorted = sortByDue
    ? [...payables].sort((a, b) => {
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;
        return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
      })
    : payables;

  const payableColumns: Column<Payable>[] = useMemo(() => [
    {
      key: "supplier_name",
      header: "NCC",
      render: (p) => (
        <div>
          <div style={{ fontWeight: 600 }}>{p.supplier_name}</div>
          {p.supplier_phone && <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>{p.supplier_phone}</div>}
        </div>
      ),
    },
    {
      key: "total_amount",
      header: "Tổng",
      align: "right",
      render: (p) => formatVND(p.total_amount),
    },
    {
      key: "paid_amount",
      header: "Đã trả",
      align: "right",
      render: (p) => <span style={{ color: "var(--accent-green)" }}>{formatVND(p.paid_amount)}</span>,
    },
    {
      key: "remaining",
      header: "Còn nợ",
      sortable: true,
      align: "right",
      render: (p) => {
        const isOverdue = p.due_date && p.status !== "paid" && new Date(p.due_date) < new Date();
        return <span style={{ color: isOverdue ? "var(--accent-red)" : "var(--accent-yellow)", fontWeight: 700 }}>{formatVND(p.remaining)}</span>;
      },
    },
    {
      key: "due_date",
      header: "Hạn trả",
      sortable: true,
      render: (p) => {
        const isOverdue = p.due_date && p.status !== "paid" && new Date(p.due_date) < new Date();
        return <span style={{ color: isOverdue ? "var(--accent-red)" : undefined }}>{p.due_date ? formatDate(p.due_date) : "—"}</span>;
      },
    },
    {
      key: "status",
      header: "Trạng thái",
      render: (p) => {
        const badge = statusBadge(p);
        return <Badge variant={badge.variant}>{badge.text}</Badge>;
      },
    },
    {
      key: "_actions",
      header: "",
      render: (p) => (
        p.status !== "paid" ? (
          <button className="btn btn-ghost" style={{ fontSize: 11, padding: "4px 8px" }} onClick={(e) => { e.stopPropagation(); openPayModal(p); }}>
            Trả tiền
          </button>
        ) : null
      ),
    },
  ], []);

  const statusBadge = (p: Payable) => {
    const isOverdue = p.due_date && p.status !== "paid" && new Date(p.due_date) < new Date();
    if (isOverdue) return { text: "Quá hạn", variant: "bad" as const };
    const map: Record<string, { text: string; variant: "ok" | "warn" | "bad" | "info" | "" }> = {
      open: { text: "Chưa trả", variant: "warn" },
      partial: { text: "Trả một phần", variant: "info" },
      paid: { text: "Đã trả", variant: "ok" },
    };
    return map[p.status] || { text: p.status, variant: "" as const };
  };

  const openPayModal = (p: Payable) => {
    setSelectedPayable(p);
    setPayAmount(p.remaining || 0);
    setPayMethod("cash");
    setPayNote("");
    setPayAttachment("");
    setShowPayModal(true);
  };

  const handleRecordPayment = async () => {
    if (!selectedPayable || payAmount <= 0) { toast.error("Số tiền phải > 0"); return; }
    setSubmitting(true);
    try {
      await api.payables.recordPayment(selectedPayable.id, {
        amount: payAmount,
        payment_method: payMethod,
        note: payNote || undefined,
        attachment_url: payAttachment || undefined,
      });
      setShowPayModal(false);
      setSelectedPayable(null);
      await loadPayables();
    } catch (e: unknown) {
      toast.error((e as Error).message || "Lỗi ghi nhận thanh toán");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="kpi-grid">
        <KpiCard label="Tổng còn nợ" value={formatVND(totalOwed)} dotColor="bad" note={`${payables.length} khoản`} />
        <KpiCard label="Quá hạn" value={overdue.length.toString()} dotColor="warn" note="cần xử lý gấp" />
        <KpiCard label="Số NCC" value={supplierCount.toString()} dotColor="blue" note="nhà cung cấp" />
      </div>

      <GlassPanel title="Công nợ nhà cung cấp" badge={`${payables.length} khoản`}>
        <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
          <div className="tabs">
            {["", "open", "partial", "paid"].map((s) => (
              <button
                key={s}
                className={`tab-item ${statusFilter === s ? "active" : ""}`}
                onClick={() => setStatusFilter(s)}
              >
                {s === "" ? "Tất cả" : s === "open" ? "Chưa trả" : s === "partial" ? "Trả 1 phần" : "Đã trả"}
              </button>
            ))}
          </div>
          <div style={{ flex: 1 }} />
          <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, cursor: "pointer" }}>
            <input type="checkbox" checked={sortByDue} onChange={(e) => setSortByDue(e.target.checked)} style={{ width: "auto" }} />
            Sắp xếp theo hạn trả
          </label>
        </div>

        {loading ? (
          <SkeletonTable rows={6} cols={6} />
        ) : error ? (
          <ErrorState error={error} onRetry={loadPayables} />
        ) : payables.length === 0 ? (
          <EmptyState icon="account_balance" title="Không có công nợ" description="Chưa có khoản phải trả nào." />
        ) : (
          <DataTable
            columns={payableColumns}
            rows={sorted}
            rowKey={(p) => String(p.id)}
            pageSize={50}
            defaultSort={{ key: "due_date", dir: "asc" }}
          />
        )}
      </GlassPanel>

      {/* Modal ghi nhận thanh toán */}
      <Modal
        open={showPayModal && !!selectedPayable}
        onClose={() => setShowPayModal(false)}
        title="Ghi nhận thanh toán"
        footer={
          <button className="btn" onClick={handleRecordPayment} disabled={submitting}>
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>payments</span>
            {submitting ? "Đang xử lý…" : "Xác nhận thanh toán"}
          </button>
        }
      >
        {selectedPayable && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ fontSize: 13, color: "var(--text-secondary)" }}>
              NCC: <strong>{selectedPayable.supplier_name}</strong> · Còn nợ: <strong style={{ color: "var(--accent-red)" }}>{formatVND(selectedPayable.remaining)}</strong>
            </div>

            <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Số tiền thanh toán *</label>
            <input
              type="number"
              value={payAmount || ""}
              onChange={(e) => setPayAmount(Number(e.target.value))}
              max={selectedPayable.remaining}
            />

            <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Phương thức thanh toán</label>
            <select value={payMethod} onChange={(e) => setPayMethod(e.target.value)}>
              <option value="cash">Tiền mặt</option>
              <option value="bank_transfer">Chuyển khoản</option>
            </select>

            <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Ghi chú</label>
            <input
              placeholder="Ghi chú thanh toán…"
              value={payNote}
              onChange={(e) => setPayNote(e.target.value)}
            />

            <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Ảnh ủy nhiệm chi (URL)</label>
            <input
              placeholder="https://..."
              value={payAttachment}
              onChange={(e) => setPayAttachment(e.target.value)}
            />
          </div>
        )}
      </Modal>

      {/* Detail modal — lịch sử thanh toán */}
      <Modal
        open={!!detailPayable}
        onClose={() => setDetailPayable(null)}
        title="Chi tiết công nợ"
        footer={
          detailPayable?.status !== "paid" ? (
            <button className="btn" onClick={() => { setDetailPayable(null); detailPayable && openPayModal(detailPayable); }}>
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>payments</span>
              Ghi nhận thanh toán
            </button>
          ) : undefined
        }
      >
        {detailPayable && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div className="list">
              <div className="list-item">
                <div className="name">Nhà cung cấp</div>
                <div className="right">{detailPayable.supplier_name}</div>
              </div>
              {detailPayable.supplier_phone && (
                <div className="list-item">
                  <div className="name">Số ĐT</div>
                  <div className="right">{detailPayable.supplier_phone}</div>
                </div>
              )}
              {detailPayable.supplier_bank_account && (
                <div className="list-item">
                  <div className="name">TK ngân hàng</div>
                  <div className="right">{detailPayable.supplier_bank_account}</div>
                </div>
              )}
              <div className="list-item">
                <div className="name">Tổng nợ</div>
                <div className="right" style={{ fontWeight: 700 }}>{formatVND(detailPayable.total_amount)}</div>
              </div>
              <div className="list-item">
                <div className="name">Đã trả</div>
                <div className="right" style={{ color: "var(--accent-green)" }}>{formatVND(detailPayable.paid_amount)}</div>
              </div>
              <div className="list-item">
                <div className="name">Còn lại</div>
                <div className="right" style={{ color: "var(--accent-red)", fontWeight: 700 }}>{formatVND(detailPayable.remaining)}</div>
              </div>
              <div className="list-item">
                <div className="name">Hạn trả</div>
                <div className="right">{detailPayable.due_date ? formatDate(detailPayable.due_date) : "Không có"}</div>
              </div>
              <div className="list-item">
                <div className="name">Trạng thái</div>
                <div className="right">
                  <Badge variant={statusBadge(detailPayable).variant}>{statusBadge(detailPayable).text}</Badge>
                </div>
              </div>
              {detailPayable.note && (
                <div className="list-item">
                  <div className="name">Ghi chú</div>
                  <div className="right" style={{ fontSize: 12 }}>{detailPayable.note}</div>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
